// Иконки действий над блоком: правка, копия, удаление, «на доску».
//
// Живут в библиотеке, а не рядом с каждым потребителем: действия над блоком
// одни и те же и в ленте библиотеки, и на таймлайне, а нарисованные дважды
// глифы однажды поправят только в одном месте. Та же причина, по которой
// здесь лежат KindIcon и SEGMENT_DND_MIME (см. index.ts).
//
// Стиль общий с KindIcon: viewBox 24×24, обводка currentColor толщиной 1.8,
// круглые концы. Цвет не задаётся никогда — иконка красится кнопкой, и на
// btn-danger та же корзина сама становится тревожной.
import type { ReactElement } from 'react';

export interface ActionIconProps {
  /** Размер в пикселях: в ряду карточки иконки крупнее, чем в столбике таймлайна. */
  size?: number;
}

function iconProps(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };
}

/** Карандаш — «изменить». */
export function EditIcon({ size = 15 }: ActionIconProps): ReactElement {
  return (
    <svg {...iconProps(size)}>
      <path d="M4 20h4L20 8l-4-4L4 16v4z" />
    </svg>
  );
}

/** Два листа — «клонировать». */
export function CloneIcon({ size = 15 }: ActionIconProps): ReactElement {
  return (
    <svg {...iconProps(size)}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
  );
}

/**
 * Корзина — «удалить из библиотеки». Именно корзина, а не крестик: крестик на
 * таймлайне значит «убрать из варианта», блок при этом остаётся жив. Дать двум
 * разным по последствиям действиям один глиф — ловушка, поэтому насовсем
 * удаляет то же, что удаляет поездку в списке поездок.
 */
export function DeleteIcon({ size = 15 }: ActionIconProps): ReactElement {
  return (
    <svg {...iconProps(size)}>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/**
 * «Положить на доску»: стрелка входит в открытую слева колонку варианта.
 *
 * Жест именно такой — блок берут из библиотеки и кладут в вариант, поэтому
 * стрелка не просто указывает вправо, а упирается в проём: одна фигура вместо
 * склейки «стрелка + плюс». Колонка открыта с той стороны, откуда приходит
 * стрелка, — закрытая рамка читалась бы как «файл», а не как «положить внутрь».
 */
export function AddToBoardIcon({ size = 15 }: ActionIconProps): ReactElement {
  return (
    <svg {...iconProps(size)}>
      <path d="M13 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-6" />
      <path d="M3 12h9" />
      <path d="M9 9l3 3-3 3" />
    </svg>
  );
}
